import { useEffect, useMemo, useState } from "react";
import { apiGet } from "../../client/apiClient.js";
import type { ApiResult, DaySlots } from "../../shared/types.js";
import { ChevronLeftIcon, ChevronRightIcon } from "../../components/Icons.js";
import { LoadingState } from "../../components/States.js";

const WEEKDAYS = ["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"];
const MONTHS = [
  "januari", "februari", "maart", "april", "mei", "juni",
  "juli", "augustus", "september", "oktober", "november", "december",
];

interface MonthDay {
  date: string;
  isOpen: boolean;
  hasAvailability: boolean;
}

function startOfMonthOffset(year: number, month: number): number {
  const jsDay = new Date(year, month - 1, 1).getDay(); // 0=zo
  return (jsDay + 6) % 7; // 0=ma
}

export function AppointmentCalendar({
  selectedDate,
  onSelectDate,
  postalCode,
}: {
  selectedDate: string | null;
  onSelectDate: (date: string) => void;
  postalCode?: string;
}) {
  const today = useMemo(() => new Date(), []);
  const [cursor, setCursor] = useState({ year: today.getFullYear(), month: today.getMonth() + 1 });
  const [days, setDays] = useState<MonthDay[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiGet<ApiResult<MonthDay[]>>(
      `/api/availability/month?year=${cursor.year}&month=${cursor.month}${postalCode ? `&postalCode=${postalCode}` : ""}`
    ).then((res) => {
      if (cancelled) return;
      if (res.ok) setDays(res.data);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [cursor, postalCode]);

  const offset = startOfMonthOffset(cursor.year, cursor.month);
  const dayMap = new Map(days.map((d) => [d.date, d]));
  const daysInMonth = new Date(cursor.year, cursor.month, 0).getDate();

  function prevMonth() {
    setCursor((c) => (c.month === 1 ? { year: c.year - 1, month: 12 } : { year: c.year, month: c.month - 1 }));
  }
  function nextMonth() {
    setCursor((c) => (c.month === 12 ? { year: c.year + 1, month: 1 } : { year: c.year, month: c.month + 1 }));
  }

  return (
    <div className="calendar">
      <div className="calendar__header">
        <button type="button" className="btn btn-ghost btn-icon" aria-label="Vorige maand" onClick={prevMonth}>
          <ChevronLeftIcon />
        </button>
        <strong>
          {MONTHS[cursor.month - 1]} {cursor.year}
        </strong>
        <button type="button" className="btn btn-ghost btn-icon" aria-label="Volgende maand" onClick={nextMonth}>
          <ChevronRightIcon />
        </button>
      </div>
      {loading ? (
        <LoadingState label="Beschikbaarheid wordt geladen…" />
      ) : (
        <div className="calendar__grid" role="grid" aria-label="Kalender">
          {WEEKDAYS.map((w) => (
            <div className="calendar__weekday" key={w} role="columnheader">
              {w}
            </div>
          ))}
          {Array.from({ length: offset }).map((_, i) => (
            <div key={`empty-${i}`} className="calendar__day calendar__day--empty" />
          ))}
          {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((d) => {
            const dateStr = `${cursor.year}-${String(cursor.month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
            const info = dayMap.get(dateStr);
            const disabled = !info || !info.isOpen || !info.hasAvailability;
            return (
              <button
                key={dateStr}
                type="button"
                className="calendar__day"
                disabled={disabled}
                aria-disabled={disabled}
                aria-pressed={selectedDate === dateStr}
                aria-label={`${d} ${MONTHS[cursor.month - 1]}${disabled ? ", niet beschikbaar" : ""}`}
                onClick={() => !disabled && onSelectDate(dateStr)}
              >
                {d}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
